import React, { useState } from "react";
import { Plus, Minus, HelpCircle } from "lucide-react";
import Reveal from "@/components/Reveal";
import JsonLd from "@/components/JsonLd";
import { faqPageSchema } from "@/lib/entity-content";

/**
 * Reusable FAQ block that renders an accordion AND emits FAQPage JSON-LD.
 * Inverted-pyramid style: direct 2-sentence answer followed by bullet points.
 */
export const FaqBlock = ({ title = "Frequently asked questions", faqs = [] }) => {
  const [open, setOpen] = useState(0);

  return (
    <section className="bg-white border-t border-[#EAEAEA]" data-testid="faq-block">
      <JsonLd data={faqPageSchema(faqs)} />
      <div className="max-w-[1024px] mx-auto px-5 md:px-8 py-20 md:py-24">
        <Reveal>
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold text-[#4A4A4A]">
              <HelpCircle size={13} className="text-[#FDD805]" />
              FAQs
            </div>
            <h2 className="mt-4 font-display text-[32px] md:text-[44px] leading-[1.05] font-black tracking-tight text-[#0F1115]">
              {title}
            </h2>
          </div>
        </Reveal>

        <div className="mt-10 divide-y divide-[#EAEAEA] border border-[#EAEAEA] rounded-2xl overflow-hidden bg-white">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q} data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-6 px-6 py-5 text-left hover:bg-[#F8F9FA] transition-colors"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-display font-bold text-[16px] md:text-[18px] text-[#0F1115]">
                    {f.q}
                  </span>
                  <span
                    className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-colors ${isOpen ? "bg-[#0F1115] text-[#FDD805]" : "bg-[#FFF9E6] text-[#0F1115]"}`}
                  >
                    {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-6 pb-7 text-[15px] leading-[1.65] text-[#1C1F26]">
                    <p className="font-medium text-[#0F1115]">{f.a}</p>
                    {f.bullets && f.bullets.length > 0 && (
                      <ul className="mt-4 space-y-2">
                        {f.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FDD805] mt-2.5 shrink-0" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FaqBlock;
